<template>
  <section class="pagination__ListWrapper container">
    <div class="filtersTopWrapper">
      <slot name="filters-top" />
    </div>

    <div v-if="endpointResponse?.count" class="pagination__ListCounter">
      <p>{{ $t(`${ counterText ? counterText : 'blogsAvailable' }`, numberOfResults) }}</p>
    </div>

    <div ref="list" v-if="endpointResponse?.results?.length">
      <div class="pagination__List">
        <slot />
      </div>

      <div class="pagination__Buttons">
        <Button v-if="showPreviousButton" @click="loadPrevious">{{ $t('loadPrevious') }}</Button>
        <nuxt-link v-if="endpointResponse?.next" :to="nextPageUrl" @click.prevent="loadMore" class="button--Primary">{{ $t('loadMore') }}</nuxt-link>
      </div>
    </div>
    <div v-else>
      <slot name="no-results">
        <p v-text="$t('noResultsBlogs')" />
      </slot>
    </div>
  </section>
  <Loader :active="fetchingData" />
</template>

<script setup>
  const props = defineProps({
    endpoint: {
      type: String,
      required: true
    },
    selectedFilters: {
      type: Object,
      default: () => ({})
    },
    counterText: {
      type: String,
      default: ''
    },
    howManyItems: {
      type: Number,
      default: 15
    },
    customApiOptions: {
      type: Object,
      default: () => ({})
    }
  });
  const emit = defineEmits(['search'])
  const config = useRuntimeConfig().public;

  const list = ref(null);
	const fetchingData = ref(true);
	const initialPaginatorPage = ref(null);
	const endpointResponse = ref(null);
	const paginationStatus = ref({
		page: 1,
		page_size: props.howManyItems,
	});

  const numberOfResults = computed(() => endpointResponse.value?.count || 0);
  const showPreviousButton = computed(() => {
    if (fetchingData.value || paginationStatus.value.page <= 1) return false;
    return initialPaginatorPage.value !== null && initialPaginatorPage.value > 1;
  });

  const nextPageUrl = computed(() => {
    if (!endpointResponse.value?.next) return null;
    const url = new URL(endpointResponse.value?.next);
    return `${ config.domain }${ useRoute().path }?${ url.searchParams.toString() }`;
  });

	async function getResults(page, queryData, client = false) {
		fetchingData.value = true;
		try {
      let response = null;
      if(client) {
        response = await useClientFetch(props.endpoint, {
          query: {
            page_size: props.howManyItems,
            page: page || paginationStatus.value.page,
            ...props.selectedFilters,
            ...props.customApiOptions,
            ...queryData
          }
        })
      }
      else {
        response = await useServerFetch(props.endpoint, {
          query: {
            page_size: props.howManyItems,
            page: page || paginationStatus.value.page,
            ...props.selectedFilters,
            ...props.customApiOptions,
            ...queryData
          }
        })
      }
      emit('search', response);
			return response;
		} catch(error) {
			console.log(error)
		} finally {
			fetchingData.value = false;
		}
	}

	async function loadMore() {
		if (!endpointResponse.value?.next) return;
		paginationStatus.value.page += 1;
		const response = await getResults(null, null, true);
		if (response?.results) {
			const _currentResults = endpointResponse.value.results || [];
			endpointResponse.value = response;
			endpointResponse.value.results = _currentResults.concat(response.results);
      emit('search', endpointResponse.value);
			updateQuery();
		}
	}

	async function loadPrevious() {
		if (paginationStatus.value.page <= 1 || initialPaginatorPage.value <= 1) return;
		initialPaginatorPage.value--;
		try {
			const response = await getResults(initialPaginatorPage.value, null, true);
			if (response.results) {
				endpointResponse.value.results = [...response.results, ...endpointResponse.value.results];
        emit('search', endpointResponse.value);
				setTimeout(() => {
					if (list.value) list.value.scrollIntoView({ behavior: 'smooth' });
				}, 10);
			}
		} catch(e) {}
	}

	function updateQuery() {
		const method = Object.keys(useRoute().query).length ? 'replace' : 'push';
		const query = {
			...paginationStatus.value,
			...props.selectedFilters,
		}
		useRouter()[method]({ query })
	}

  async function filterData() {
    setTimeout(async () => {
			paginationStatus.value.page = 1;
			const response = await getResults(null, null, true);
			updateQuery();
			endpointResponse.value = response;
    }, 0);
  }
	
  if (useRoute().query?.page) {
    paginationStatus.value.page = parseInt(useRoute().query.page);
    initialPaginatorPage.value = paginationStatus.value.page;
  }

  /*try {
    const response = await getResults(null, {
      ...useRoute().query,
    });
    if (response) {
      endpointResponse.value = response;
    }
  } catch(error) {}*/

  onMounted(() => {
    setTimeout(async () => {
      try {
        const response = await getResults(null, {
          ...useRoute().query,
        }, true);
        if (response) {
          endpointResponse.value = response;
        }
      } catch(error) {}
    }, 10);
  })

  defineExpose({
    filterData,
  });
</script>

<style lang="scss">
	@import url('@/assets/styles/components/_pagination.scss');
</style>